import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Button, Card, Heading, LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanPublicBlogList,
  fetchYowanPublicTopPageSlots,
} from '../app/yowanContentClient';
import BaseStaticPage from './BaseStaticPage';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

const HOME_BLOG_PREVIEW_LIMIT = 5;
const YOWAN_GOOGLE_CALENDAR_EMBED_INPUT = process.env.REACT_APP_YOWAN_GOOGLE_CALENDAR_EMBED_URL || '';

const decodeCalendarAttributeValue = (value = '') =>
  String(value)
    .replace(/&amp;/g, '&')
    .replace(/&#38;/g, '&')
    .trim();

const getYowanGoogleCalendarEmbedUrl = (value = '') => {
  const rawValue = String(value || '').trim();
  const srcMatch = rawValue.match(/\bsrc=["']([^"']+)["']/i);
  const candidate = decodeCalendarAttributeValue(srcMatch ? srcMatch[1] : rawValue);

  if (!candidate) {
    return '';
  }

  try {
    const parsedUrl = new URL(candidate);

    if (parsedUrl.protocol !== 'https:' || parsedUrl.hostname !== 'calendar.google.com') {
      return '';
    }

    return parsedUrl.toString();
  } catch (error) {
    return '';
  }
};

const getHomeSlotImageSrc = (image = null) => {
  if (!image) {
    return '';
  }

  return image.preview_url || image.previewUrl || image.url || image.thumbnail_url || image.thumbnailUrl || '';
};

const buildHomeSlotMap = (slots = []) =>
  slots.reduce((accumulator, slot) => {
    if (slot?.slotKey) {
      accumulator[slot.slotKey] = slot;
    }

    return accumulator;
  }, {});

const formatHomeBlogDate = (value = '') => {
  if (!value) {
    return '';
  }

  return String(value).replace(/-/g, '.');
};

const HomeSlotImage = ({ className, imageClassName, slot, fallbackLabel, fallbackAlt, isLoading = false }) => {
  const imageSrc = getHomeSlotImageSrc(slot?.primary_image);
  const altText = slot?.primary_image?.alt_text || slot?.title || fallbackAlt || '';

  return (
    <div
      className={`${className}${imageSrc ? ` ${imageClassName}` : ''}`}
      aria-hidden={imageSrc || isLoading ? undefined : true}
      aria-busy={isLoading ? 'true' : undefined}
    >
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={altText}
          loading="lazy"
          decoding="async"
        />
      ) : isLoading ? (
        <LoadingSpinner className="yowan-public-image-loading" />
      ) : (
        <span className="yowan-public-image-fallback-label">{fallbackLabel}</span>
      )}
    </div>
  );
};

const HomePage = () => {
  const content = yowanPageContent['/'];
  const metadata = getYowanRouteMeta('/');
  const location = useLocation();
  const [pageSlots, setPageSlots] = useState({});
  const [isPageSlotsLoading, setIsPageSlotsLoading] = useState(true);
  const [latestBlogEntries, setLatestBlogEntries] = useState([]);
  const [isLatestBlogLoading, setIsLatestBlogLoading] = useState(true);
  const googleCalendarEmbedUrl = useMemo(
    () => getYowanGoogleCalendarEmbedUrl(YOWAN_GOOGLE_CALENDAR_EMBED_INPUT),
    [],
  );

  useEffect(() => {
    let isMounted = true;

    setIsLatestBlogLoading(true);
    fetchYowanPublicBlogList({ page: 1, pageSize: HOME_BLOG_PREVIEW_LIMIT })
      .then((payload) => {
        if (isMounted) {
          setLatestBlogEntries(payload.results || []);
          setIsLatestBlogLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setLatestBlogEntries([]);
          setIsLatestBlogLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    setIsPageSlotsLoading(true);
    fetchYowanPublicTopPageSlots()
      .then((payload) => {
        if (isMounted) {
          setPageSlots(buildHomeSlotMap(payload.results));
        }
      })
      .catch(() => {
        if (isMounted) {
          setPageSlots({});
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsPageSlotsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const slots = useMemo(() => ({
    hero: pageSlots.hero || null,
    overview: pageSlots.overview || null,
    updates: pageSlots.updates || null,
  }), [pageSlots]);

  return (
    <>
      <BaseStaticMeta metadata={metadata} />
      <Section className="yowan-home-hero">
        <PageContainer>
          <HomeSlotImage
            className="yowan-home-hero__banner"
            imageClassName="yowan-home-static-image yowan-home-static-image--hero"
            slot={slots.hero}
            fallbackLabel="main visual area"
            fallbackAlt={content.hero.title}
            isLoading={isPageSlotsLoading}
          />
          <div className="yowan-home-hero__copy">
            <Heading as="h1" align="center">{content.hero.title}</Heading>
            <p className="yowan-home-hero__subtitle">{content.hero.subtitle}</p>
          </div>
        </PageContainer>
      </Section>
      <Section>
        <PageContainer>
          <div className="yowan-home-overview">
            <div className="yowan-home-overview__copy c-surface-content">
              <Heading as="h2">
                {content.overview.title}
              </Heading>
              <p className="yowan-home-overview__lead">
                揺腕法は「ボディワーク 刀禅」の創始者・
                <Link
                  className="yowan-home-overview__inline-link"
                  to={appendYowanPreviewQueryIfNeeded('/founder', location.search)}
                >
                  小用茂夫（Shigeo Koyou）
                </Link>
                が発表した、新しい身体運動法です。
              </p>
              <div className="yowan-home-overview__body u-stack-md">
                {content.overview.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>
            <HomeSlotImage
              className="yowan-home-overview__figure"
              imageClassName="yowan-home-static-image yowan-home-static-image--overview"
              slot={slots.overview}
              fallbackLabel="diagram area"
              fallbackAlt={content.overview.title}
              isLoading={isPageSlotsLoading}
            />
          </div>
        </PageContainer>
      </Section>
      <Section>
        <PageContainer>
          <div className="yowan-home-updates">
            <Heading as="h2" align="center">{content.updates.title}</Heading>
            <div className="yowan-home-updates__layout">
              <HomeSlotImage
                className="yowan-home-updates__image"
                imageClassName="yowan-home-static-image yowan-home-static-image--updates"
                slot={slots.updates}
                fallbackLabel="book image area"
                fallbackAlt={content.updates.title}
                isLoading={isPageSlotsLoading}
              />
              <div className="yowan-home-updates__cards">
                {content.updates.cards.map((card) => (
                  <Card key={card.title} className="yowan-home-updates__card">
                    <Heading as="h3">{card.title}</Heading>
                    <p>{card.body}</p>
                    <Button
                      as={card.external ? 'a' : Link}
                      {...(card.external
                        ? { href: card.href, target: '_blank', rel: 'noreferrer' }
                        : { to: appendYowanPreviewQueryIfNeeded(card.href, location.search) })}
                      variant="secondary"
                    >
                      {card.ctaLabel}
                    </Button>
                  </Card>
                ))}
              </div>
            </div>
            <div className="yowan-home-blog-preview">
              <div className="yowan-home-blog-preview__list">
                {isLatestBlogLoading ? (
                  <div className="yowan-home-blog-preview__item">
                    <span className="yowan-home-blog-preview__date" aria-hidden="true">-</span>
                    <span className="yowan-home-blog-preview__title">
                      <LoadingSpinner size="sm" />
                    </span>
                  </div>
                ) : latestBlogEntries.length > 0 ? (
                  latestBlogEntries.map((item) => (
                    <Link
                      key={item.id || item.slug || `${item.date}-${item.title}`}
                      className="yowan-home-blog-preview__item"
                      to={appendYowanPreviewQueryIfNeeded(item.href || '/blog', location.search)}
                    >
                      <span className="yowan-home-blog-preview__date">{formatHomeBlogDate(item.date)}</span>
                      <span className="yowan-home-blog-preview__title">{item.title}</span>
                    </Link>
                  ))
                ) : (
                  <div className="yowan-home-blog-preview__item">
                    <span className="yowan-home-blog-preview__date">-</span>
                    <span className="yowan-home-blog-preview__title">ブログ記事はまだありません。</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </PageContainer>
      </Section>
      <Section>
        <PageContainer>
          <div className="yowan-home-schedule">
            <Heading as="h2" align="center">{content.schedule.title}</Heading>
            <p className="yowan-home-schedule__lead">
              {content.schedule.lead}
              <Link
                className="yowan-home-schedule__link"
                to={appendYowanPreviewQueryIfNeeded(content.schedule.href, location.search)}
              >
                {content.schedule.linkLabel}
              </Link>
              {content.schedule.bridge}
              <br />
              {content.schedule.tail}
            </p>
            <div
              className={`yowan-home-schedule__calendar${googleCalendarEmbedUrl ? ' yowan-home-schedule__calendar--ready' : ' yowan-home-schedule__calendar--empty'}`}
            >
              {googleCalendarEmbedUrl ? (
                <iframe
                  className="yowan-home-schedule__calendar-frame"
                  src={googleCalendarEmbedUrl}
                  title={content.schedule.calendarTitle}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : (
                <span>{content.schedule.calendarFallbackLabel}</span>
              )}
            </div>
          </div>
        </PageContainer>
      </Section>
      <Section>
        <PageContainer>
          <div className="yowan-home-contact">
            <div className="yowan-content-section__cards">
              {content.contact.cards.map((card) => (
                <Card key={card.title} className="yowan-content-section__card">
                  <Heading as="h3">{card.title}</Heading>
                  <p>{card.body}</p>
                  <Link
                    className="yowan-content-section__card-link"
                    to={appendYowanPreviewQueryIfNeeded(card.href, location.search)}
                  >
                    {card.ctaLabel || '詳しく見る'}
                  </Link>
                </Card>
              ))}
            </div>
          </div>
        </PageContainer>
      </Section>
    </>
  );
};

const BaseStaticMeta = ({ metadata }) => {
  return metadata ? <BaseStaticPage content={null} metadata={metadata} /> : null;
};

export default HomePage;
